/*
 * Short text field component
 */
import React from 'react';

import Base from './base.jsx';
import {
  DataType,
  Mode,
  Format,
  castDataType,
  NumericalValidationLimits,
  NumberFormatting,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import PeriodSelect from '../../common/PeriodSelect.jsx'

const periodTypes = [ 
    {label: I18n.t('monthly'), key: 'monthly'},
    {label: I18n.t('quarterly'), key: 'quarterly'},
    {label: I18n.t('weekly'), key: 'weekly'},
    {label: I18n.t('yearly'), key: 'yearly'}
]

const rangeTypes = [
    {label: I18n.t('fixed_range', {scope: 'fe'}), key: 'fixed'},
    {label: I18n.t('dynamic_range', {scope: 'fe'}), key: 'dynamic'},
]

export default class PeriodField extends Base {

        static get CATEGORY() {
            return 'question';
        }

        static get TYPE() {
            return 'period_field';
        }

        static icon(){
            return <i className="fa fa-calendar"></i>;
        }

        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label',
                placeholder: 'field_default_placeholder',
                sub_placeholder: 'field_default_placeholder',
                description: 'field_default_description',
                help: null,
                unit: null
            };
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                placeholder: 'placeholder',
                sub_placeholder: 'placeholder',
                description: 'description',
                name: 'field_' + Helpers.randomString(),
                reportKey: '',
                help: 'help',
                required: false,
                periodType: 'yearly',
                rangeType: 'fixed',
                label_hidden: false,
                dataType: DataType.STRING,
                validations: {},
                lockEditWhenPrefilled: false,
                unit: 'unit',
                sourcesOfPrefill: '',
                prettyView: {
                    hide: false,
                    show_label: false
                }
            });
        }

        static compatiblePrefillComponents() {
          return ['text_field', 'dropdown_selection', 'period_field'];
        }
      
        static compatiblePrefillDataTypes() {
          return [DataType.STRING];
        }

        renderView() {
            let validationFailures = this.getFormValidationFailures();

            return (
                <div>
                    {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                            <span className="field-label">{this.getText('label')}</span>
                            {this.props.content.required ? <span className='required-field'> *</span> : null}
                            {this.helpIcon()}
                            {this.renderPrefillIcon()}
                        </label>

                        <div className="input-container">
                            <PeriodSelect 
                                className={validationFailures.length > 0 ? `form-control has-error warning-tooltip ${this.isReadOnlyAnswer() ? 'locked' : ''} ${this.getInputClassNames()}` : `form-control ${this.isReadOnlyAnswer() ? 'locked' : ''} ${this.getInputClassNames()}`}
                                recurrenceType={this.props.content.periodType}
                                yearRangeSettings={{
                                    rangeType: this.props.content.rangeType,
                                    startYear: this.props.content.startYear,
                                    endYear: this.props.content.endYear,
                                    yearsInPast: this.props.content.yearsInPast,
                                    yearsInFuture: this.props.content.yearsInFuture
                                }}
                                onChange={(val) => this.updateAnswer(val)}
                                value={this.getRawAnswer()}
                                placeholders={[this.getText('placeholder'), this.getText('sub_placeholder')]}
                                language={this.props.language}
                                data-validation={validationFailures.length > 0}
                                disabled={this.props.content.disabled === true || this.props.mode != Mode.FILL || this.isReadOnlyAnswer()}
                            />
                            <input 
                                type='hidden'
                                name={this.fullFieldName()}
                                value={this.getRawAnswer()}
                            />

                        </div>
                        {this.descriptionText()}
                        {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
                    </>)}
                </div>
            );
        }

        renderSettings() {
            
            return (
                <div>
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
                    <Setting.RadioSelection 
                        name="periodType" 
                        options={periodTypes} 
                        label={I18n.t('field_period_type', { scope: 'fe' })} 
                        selectedOption={this.props.content.periodType} 
                        onChange={this.updateSettings} 
                    />

                    <Setting.RadioSelection
                        name="rangeType"
                        options={rangeTypes}
                        label={I18n.t('field_range_type', { scope: 'fe' })}
                        selectedOption={this.props.content.rangeType}
                        onChange={this.updateSettings}
                    />

                    <div className="setting">
                      <label>{I18n.t('field_range', {scope: 'fe'})}</label>
                      <div className="option-groups">
                        <div className="option-group">
                          <div className="row">
                            <div className="col-xs-6">
                              <div className="form-group mb-0">
                                {this.props.content.rangeType === 'fixed' ?
                                  <Setting.TextField name="startYear" label={I18n.t('field_start_year', {scope: 'fe'})} value={this.props.content.startYear} onChange={this.updateSettings} /> :
                                  <Setting.TextField name="yearsInPast" label={I18n.t('field_years_in_past', {scope: 'fe'})} value={this.props.content.yearsInPast} onChange={this.updateSettings} />
                                }
                              </div>
                            </div>
                            <div className="col-xs-6">
                              <div className="form-group mb-0">
                                {this.props.content.rangeType === 'fixed' ?
                                  <Setting.TextField name="endYear" label={I18n.t('field_end_year', {scope: 'fe'})} value={this.props.content.endYear} onChange={this.updateSettings} /> :
                                  <Setting.TextField name="yearsInFuture" label={I18n.t('field_years_in_future', {scope: 'fe'})} value={this.props.content.yearsInFuture} onChange={this.updateSettings} />
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
                    <Setting.TextField name="sub_placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('sub_placeholder')} onChange={this.updateSettings} />
                    <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
                    <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />

                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />

                    <Setting.Checkbox name="lockEdit" label={I18n.t('field_lock_edit', {scope: 'fe'})} description={I18n.t('field_lock_edit_description', {scope: 'fe'})} value={this.props.content.lockEdit} onChange={this.updateSettings} />
                    {(!this.props.settings['hideLockEditWhenPrefilled'] && AppConfig.features.form_prefill) &&
                      <Setting.Checkbox name="lockEditWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_edit_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockEditWhenPrefilled} onChange={this.updateSettings} />
                    }
                </div>
            );
        }

        beforeUpdateSettings(content) {
            content.required = castDataType(content.required, DataType.BOOLEAN);
            content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
            content.lockEditWhenPrefilled = castDataType(content.lockEditWhenPrefilled, DataType.BOOLEAN);
            content.lockEdit = castDataType(content.lockEdit, DataType.BOOLEAN);

            if (content.startYear)
                content.startYear = castDataType(content.startYear, DataType.NUMBER);
            if (content.endYear)
                content.endYear = castDataType(content.endYear, DataType.NUMBER);
            if (content.yearsInPast)
                content.yearsInPast = castDataType(content.yearsInPast, DataType.NUMBER);
            if (content.yearsInFuture)
                content.yearsInFuture = castDataType(content.yearsInFuture, DataType.NUMBER);
        }

}
